import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import axios from 'axios';
import './Login.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  // forgot-password state: null | 'request' | 'verify'
  const [fpStep, setFpStep]             = useState(null);
  const [fpIdentifier, setFpIdentifier] = useState('');
  const [fpOtp, setFpOtp]               = useState('');
  const [fpNewPwd, setFpNewPwd]         = useState('');
  const [fpConfirm, setFpConfirm]       = useState('');
  const [fpPhoneHint, setFpPhoneHint]   = useState('');
  const [fpMsg, setFpMsg]               = useState('');
  const [showFpPwd, setShowFpPwd]       = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const userData = await login(username, password);
      if (!userData.profile_completed && userData.user_type !== 'S') {
        navigate('/know-you-better', { replace: true });
      } else {
        navigate('/categories', { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Invalid username or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFpRequest = async (e) => {
    e.preventDefault();
    setError(''); setFpMsg('');
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/authRoutes/forgot-password/request`, { identifier: fpIdentifier });
      setFpPhoneHint(res.data.phone_hint);
      setFpStep('verify');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFpReset = async (e) => {
    e.preventDefault();
    setError(''); setFpMsg('');
    if (fpNewPwd !== fpConfirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/authRoutes/forgot-password/reset`, {
        identifier: fpIdentifier,
        otp: fpOtp,
        new_password: fpNewPwd,
      });
      setFpMsg('Password reset successfully! You can now sign in.');
      setFpStep(null);
      setFpIdentifier(''); setFpOtp(''); setFpNewPwd(''); setFpConfirm('');
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const cancelFp = () => {
    setFpStep(null); setError(''); setFpMsg('');
    setFpIdentifier(''); setFpOtp(''); setFpNewPwd(''); setFpConfirm('');
  };

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="background-shape shape-1"></div>
        <div className="background-shape shape-2"></div>
        <div className="background-shape shape-3"></div>
      </div>

      <div className="login-card">
        <div className="login-header">
          <img src="/logo.webp" alt="The New Luxury" className="login-logo" />
        </div>

        {fpStep === null && (
          <>
            <div className="welcome-text">
              <h2>Welcome Back!</h2>
              <p>Sign in to continue shopping</p>
            </div>

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span><span>{error}</span>
              </div>
            )}
            {fpMsg && (
              <div className="fp-success-msg">
                <span>✅</span><span>{fpMsg}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="username">Phone / Email / Username</label>
                <div className="input-wrapper">
                  <span className="input-icon">👤</span>
                  <input type="text" id="username" value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Phone, email or username" required disabled={loading}
                    autoComplete="username" />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="input-wrapper">
                  <span className="input-icon">🔒</span>
                  <input type={showPassword ? 'text' : 'password'} id="password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password" required disabled={loading}
                    autoComplete="current-password" />
                  <button type="button" className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading} tabIndex={-1}>
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              <div className="fp-link-row">
                <button type="button" className="fp-link"
                  onClick={() => { setError(''); setFpMsg(''); setFpStep('request'); }}>
                  Forgot password?
                </button>
              </div>

              <button type="submit" className="login-button" disabled={loading}>
                {loading ? <><span className="spinner"></span><span>Signing in...</span></> : 'Sign In'}
              </button>
            </form>

            <div className="login-register">
              New here?{' '}
              <button className="login-register-link" onClick={() => navigate('/register')}>
                Create an account
              </button>
            </div>
          </>
        )}

        {fpStep === 'request' && (
          <>
            <div className="welcome-text">
              <h2>Forgot Password</h2>
              <p>Enter your phone number — we'll send an OTP via WhatsApp</p>
            </div>

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span><span>{error}</span>
              </div>
            )}

            <form onSubmit={handleFpRequest} className="login-form">
              <div className="form-group">
                <label>Phone / Email / Username</label>
                <div className="input-wrapper">
                  <span className="input-icon">👤</span>
                  <input type="text" value={fpIdentifier}
                    onChange={(e) => setFpIdentifier(e.target.value)}
                    placeholder="Phone, email or username" required disabled={loading}
                    autoComplete="username" />
                </div>
              </div>

              <button type="submit" className="login-button" disabled={loading || !fpIdentifier}>
                {loading ? <><span className="spinner"></span><span>Sending OTP...</span></> : 'Send OTP via WhatsApp'}
              </button>
              <button type="button" className="fp-back-btn" onClick={cancelFp}>
                ← Back to Sign In
              </button>
            </form>
          </>
        )}

        {fpStep === 'verify' && (
          <>
            <div className="welcome-text">
              <h2>Enter OTP</h2>
              <p>OTP sent to <strong>{fpPhoneHint}</strong> via WhatsApp</p>
            </div>

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span><span>{error}</span>
              </div>
            )}

            <form onSubmit={handleFpReset} className="login-form">
              <div className="form-group">
                <label>OTP</label>
                <div className="input-wrapper">
                  <span className="input-icon">🔑</span>
                  <input type="text" inputMode="numeric" maxLength={6}
                    value={fpOtp} onChange={(e) => setFpOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="6-digit OTP" required disabled={loading} />
                </div>
              </div>

              <div className="form-group">
                <label>New Password</label>
                <div className="input-wrapper">
                  <span className="input-icon">🔒</span>
                  <input type={showFpPwd ? 'text' : 'password'}
                    value={fpNewPwd} onChange={(e) => setFpNewPwd(e.target.value)}
                    placeholder="New password (min 4 chars)" required disabled={loading}
                    autoComplete="new-password" />
                  <button type="button" className="password-toggle"
                    onClick={() => setShowFpPwd(!showFpPwd)} tabIndex={-1}>
                    {showFpPwd ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Confirm Password</label>
                <div className="input-wrapper">
                  <span className="input-icon">🔒</span>
                  <input type={showFpPwd ? 'text' : 'password'}
                    value={fpConfirm} onChange={(e) => setFpConfirm(e.target.value)}
                    placeholder="Repeat new password" required disabled={loading}
                    autoComplete="new-password" />
                </div>
              </div>

              <button type="submit" className="login-button"
                disabled={loading || !fpOtp || !fpNewPwd || !fpConfirm}>
                {loading ? <><span className="spinner"></span><span>Resetting...</span></> : 'Reset Password'}
              </button>
              <button type="button" className="fp-back-btn"
                onClick={() => { setFpStep('request'); setError(''); setFpOtp(''); setFpNewPwd(''); setFpConfirm(''); }}>
                ← Resend OTP
              </button>
            </form>
          </>
        )}

        <div className="login-footer">
          <p>© {new Date().getFullYear()} The New Luxury. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
